<template>
  <div class="export-grid-button">
    <GuDialog>
      <GuDialogTrigger as-child>
        <GuButton
          variant="secondary"
          size="sm"
          icon="bx-download"
          aria-label="Export table data as CSV or Excel file"
          @click="setTimeStamp"
        />
      </GuDialogTrigger>

      <GuDialogContent>
        <template #title>
          <h5 class="modal-title fs-5">
            {{ props.header }}
          </h5>
        </template>

        <div class="mb-3">
          <GuLabel for="file-name">File name:</GuLabel>
          <GuInputGroup>
            <GuInput id="file-name" v-model="fileName" />
            <GuInputGroupText>{{ extension }}</GuInputGroupText>
          </GuInputGroup>
        </div>

        <div class="mb-3">
          <GuLabel for="field-type">File type:</GuLabel>
          <GuRadioGroup v-model="selectedFileType" class="toggle-blocks" orientation="vertical">
            <label class="btn btn-toggle">
              <GuRadioGroupItem :value="EXPORT_FILE_TYPES.XLS" />
              Excel (.xls)
            </label>
            <label class="btn btn-toggle">
              <GuRadioGroupItem :value="EXPORT_FILE_TYPES.CSV" />
              Comma-separated values (.csv)
            </label>
          </GuRadioGroup>
        </div>

        <GuRadioGroup
          v-if="rowModelType === 'clientSide'"
          v-model="exportOptions"
          class="toggle-blocks"
          orientation="vertical"
        >
          <GuLabel for="field-type">Export Options:</GuLabel>
          <label class="btn btn-toggle">
            <GuRadioGroupItem :value="EXPORT_OPTIONS.ALL_ROWS" />
            All Rows
            <div class="form-text">Export all rows</div>
          </label>
          <label v-if="multiSelectEnabled" class="btn btn-toggle">
            <GuRadioGroupItem :value="EXPORT_OPTIONS.SELECTED_ROWS" />
            Selected Rows
            <div class="form-text">Export only selected rows</div>
          </label>
          <label v-if="admin" class="btn btn-toggle">
            <GuRadioGroupItem :value="EXPORT_OPTIONS.ALL_COLUMNS" />
            All Columns
            <div class="form-text">Export all columns (including hidden)</div>
          </label>
        </GuRadioGroup>

        <template #footer>
          <GuDialogClose as-child>
            <GuButton variant="link">
              Cancel
            </GuButton>
          </GuDialogClose>

          <GuButton
            icon="bx-download"
            :disabled="isExporting"
            @click.prevent="handleExport"
          >
            {{ isExporting ? 'Exporting...' : 'Export' }}
          </GuButton>
        </template>
      </GuDialogContent>
    </GuDialog>

    <GuToastProvider />
  </div>
</template>

<script setup>
import { computed, ref, inject } from 'vue'
import * as XLSX from 'xlsx'

import { error } from '@shared/global/console-messages'
import { formatDate } from '@shared/utils/date'
import { GuButton } from '@shared/gu-components/button'
import { GuDialog, GuDialogClose, GuDialogContent, GuDialogTrigger } from '@shared/gu-components/dialog'
import { GuInput } from '@shared/gu-components/input'
import { GuInputGroup, GuInputGroupText } from '@shared/gu-components/input-group'
import { GuLabel } from '@shared/gu-components/label'
import { GuRadioGroup, GuRadioGroupItem } from '@shared/gu-components/radio-group'
import { GuToastProvider, useToast } from '@shared/gu-components/toast'
import { httpRequest } from '@shared/api/httpRequests'

import { injectRowModelType } from '@gu-datagrid-new/composables/RowModelType'

const props = defineProps({
  admin: {
    type: Boolean,
    default: false,
    description: 'Whether the user is an administrator',
  },
  apiSettings: {
    type: Object,
    default: () => {},
    description: 'API settings',
  },
  config: {
    type: Object,
    default: () => {},
    description: 'Datagrid configuration (both GU props and Ag-Grid options)',
  },
  header: {
    type: String,
    default: 'Export Projects',
    description: 'Header text, such as "Export 100 projects".',
  },
  path: {
    type: String,
    default: '',
    description: 'Path for the ajax post (to which other URL params will be appended).',
  },
  defaultFilename: {
    type: String,
    default: 'Export',
    description: 'Default filename (excluding exclusion), such as "Projects Export".',
  },
  multiSelectEnabled: {
    type: Boolean,
    default: false,
    description: 'Whether the multi-select is enabled',
  },
  queueName: {
    type: String,
    default: 'NOT_SET',
    description: 'Required field for server to determine data to return for a specific queue',
  },
  gridApi: {
    type: Object,
    default: () => {},
  },
})

const EXPORT_OPTIONS = {
  ALL_ROWS: 'allRows',
  SELECTED_ROWS: 'selectedRows',
  ALL_COLUMNS: 'allColumns',
}

const EXPORT_FILE_TYPES = {
  XLS: 'xls',
  CSV: 'csv',
}

const rowModelType = injectRowModelType()
const gridState = inject('gridState', null)
const { toast } = useToast()

const fileName = ref(props.defaultFilename)
const selectedFileType = ref('xls')
const extension = computed(() => `.${selectedFileType.value}`)
const timeStamp = ref('')
const exportOptions = ref('allRows')
const isExporting = ref(false)
const hasSelection = computed(() => props.gridApi?.getSelectedNodes().length > 0)

const setTimeStamp = () => {
  const date = new Date()
  timeStamp.value = formatDate(date, 'YYYY-MM-DD')
}

const getTotalRowCount = () => {
  if (!props.gridApi) return 0
  return props.gridApi.paginationGetRowCount() || 0
}

const fetchAllRowsForExport = async (rowCount) => {
  if (rowCount > 1000) return null

  const payload = {
    agGrid: {
      startRow: 0,
      endRow: rowCount,
      rowGroupCols: [],
      valueCols: [],
      pivotCols: [],
      pivotMode: false,
      groupKeys: [],
      filterModel: props.gridApi?.getFilterModel?.() || {},
      sortModel: props.gridApi?.getSortModel?.() || [],
    },
    queueName: props.queueName,
    quickFilterValue: gridState?.quickFilterValue?.value || '',
  }

  const rowsUrl = props.config.config.rowsUrl || props.apiSettings.rowsUrl

  try {
    const response = await httpRequest({
      url: rowsUrl,
      method: 'POST',
      data: payload,
      headers: { 'Content-Type': 'application/json' },
    })

    return response.rowData || []
  } catch (err) {
    error({ context: 'Export', message: `Failed to fetch all rows for export: ${err.message ?? err}` })
    toast({ title: 'Error', description: 'Could not fetch all rows for export.', variant: 'destructive' })
    return null
  }
}
const downloadExportFromEndpoint = async (endpoint, fileName) => {
  const columnState = props.gridApi?.getColumnState?.() || []
  const filteredColumnState = columnState.filter(col =>
    col.colId !== 'ag-Grid-SelectionColumn' &&
    col.colId !== 'id-checkbox-selection',
  )

  const payload = {
    agGrid: {
      filterModel: props.gridApi?.getFilterModel?.() || {},
      sortModel: props.gridApi?.getSortModel?.() || [],
      columnState: filteredColumnState,
    },
    fileName: fileName.replace(extension.value, ''),
    fileExtension: selectedFileType.value,
    queueName: props.queueName,
    quickFilterValue: gridState?.quickFilterValue?.value || '',
  }

  const customHeaders = {
    csv: 'text/csv',
    xls: 'application/vnd.ms-excel',
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        Accept: customHeaders[selectedFileType.value],
      },
      body: JSON.stringify(payload),
    })

    if (response.status === 204) {
      throw new Error('You can only export up to 500 items. Please update filters and try again')
    }

    if (response.status !== 200) {
      throw new Error(`Status: ${response.status} | StatusText: ${response.statusText}`)
    }

    const blob = await response.blob()
    downloadFile(blob, fileName)
    return true
  } catch (err) {
    error({ context: 'Export', message: `Failed to export via endpoint: ${err.message ?? err}` })
    toast({ title: 'Error', description: err.message || 'Export failed. Please try again.', variant: 'destructive' })
    return false
  }
}

const convertToCsv = (rows, gridApi) => {
  const columns = exportOptions.value === EXPORT_OPTIONS.ALL_COLUMNS
    ? gridApi.getAllColumns()
    : gridApi.getAllDisplayedColumns()

  const validColumns = columns.filter(col =>
    col.getColId() !== 'ag-Grid-SelectionColumn' &&
    !/actions/i.test(col.getColDef()?.headerName),
  )

  const headers = validColumns.map(col => col.getColDef().headerName || col.getColId()).join(',')

  const data = rows.map(row =>
    validColumns.map(col => `"${row[col.getColId()] || ''}"`).join(','),
  ).join('\n')

  return `${headers}\n${data}`
}

const convertToExcel = (rows, gridApi) => {
  const columns = exportOptions.value === EXPORT_OPTIONS.ALL_COLUMNS
    ? gridApi.getAllColumns()
    : gridApi.getAllDisplayedColumns()

    const validColumns = columns.filter(col =>
    col.getColId() !== 'ag-Grid-SelectionColumn' &&
    !/actions/i.test(col.getColDef()?.headerName),
  )

  const headers = validColumns.map(col => col.getColDef().headerName || col.getColId())
  const data = rows.map(row => validColumns.map(col => row[col.getColId()] || ''))
  const wsData = [headers, ...data]
  const ws = XLSX.utils.aoa_to_sheet(wsData)
  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')

  return XLSX.write(wb, { bookType: 'xls', type: 'array' })
}

const downloadFile = (content, fileName) => {
  const type = fileName.endsWith('.csv') ? 'text/csv' : 'application/vnd.ms-excel'
  const blob = new Blob([content], { type })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  window.URL.revokeObjectURL(url)
}

const getExportableColumnKeys = () => {
  const columns = exportOptions.value === EXPORT_OPTIONS.ALL_COLUMNS
    ? props.gridApi.getAllColumns()
    : props.gridApi.getAllDisplayedColumns()

    return columns
    .filter(col => col.getColId() !== 'ag-Grid-SelectionColumn' && !/actions/i.test(col.getColDef()?.headerName))
    .map(col => col.getColId())
}

const toastError = (message) => {
  toast({ title: 'Error', description: message, variant: 'destructive' })
  isExporting.value = false
}

const toastSuccess = (message) => {
  toast({ title: 'Success', description: message, variant: 'success' })
}

const createExportParams = (fileName) => ({
  fileName,
  allColumns: exportOptions.value === EXPORT_OPTIONS.ALL_COLUMNS,
  columnKeys: getExportableColumnKeys(),
  processCellForExport: (params) => {
    const colDef = params.column.getColDef()
    if (colDef && (/actions/i.test(colDef.headerName) || params.column.getColId() === 'ag-Grid-SelectionColumn')) {
      return null
    }
    return params.value
  },
})

const fetchDataForExport = async (rowCount) => {
  if (rowModelType.value !== 'serverSide' || exportOptions.value !== EXPORT_OPTIONS.ALL_ROWS) return null
  return await fetchAllRowsForExport(rowCount)
}

const handleExportOption = (exportParams, allRowsData, fileName) => {
  if (exportOptions.value === EXPORT_OPTIONS.SELECTED_ROWS) {
    if (!hasSelection.value) {
      toast({ title: 'Warning', description: 'Please select at least one row to export.', variant: 'warning' })
      return
    }
    exportParams.onlySelected = true
  } else if (exportOptions.value === EXPORT_OPTIONS.ALL_ROWS) {
    if (rowModelType.value === 'serverSide' && allRowsData) {
      const content = selectedFileType.value === EXPORT_FILE_TYPES.CSV
        ? convertToCsv(allRowsData, props.gridApi)
        : convertToExcel(allRowsData, props.gridApi)
      downloadFile(content, selectedFileType.value === EXPORT_FILE_TYPES.CSV ? fileName : fileName.replace('.csv', '.xls'))
    } else {
      exportParams.allColumns = exportOptions.value === EXPORT_OPTIONS.ALL_COLUMNS
      if (rowModelType.value === 'serverSide') exportParams.getRowHeight = () => 25
    }
  }

  if (!allRowsData) {
    if (selectedFileType.value === EXPORT_FILE_TYPES.CSV) props.gridApi.exportDataAsCsv(exportParams)
    if (selectedFileType.value === EXPORT_FILE_TYPES.XLS) props.gridApi.exportDataAsExcel(exportParams)
  }
}

const handleExportError = (err) => {
  error({ context: 'Export', message: `Error export grid data: ${err.message ?? err}` })
  toast({ title: 'Error', description: 'Export failed. Please try again.', variant: 'destructive' })
}

const handleExport = async () => {
  if (!props.gridApi) return
  isExporting.value = true

  const formattedFileName = `${fileName.value}_${timeStamp.value}${extension.value}`.replace(/ /g, '_')

  const fileExportEndpoint = props.config?.exportButtonSettings?.endpoint

  // Use implementation with file export endpoint if provided in content-override
  if (fileExportEndpoint) {
    try {
      const success = await downloadExportFromEndpoint(fileExportEndpoint, formattedFileName)

      if (success) toastSuccess(`File exported successfully: ${formattedFileName}`)
    } catch (err) {
      handleExportError(err)
    } finally {
      isExporting.value = false
    }
    return
  }

  // Use implementation with rowsUrl
  const rowCount = getTotalRowCount()
  if (rowCount > 1000) {
    toastError('Cannot export more than 1,000 rows. Please, update filters and try again.')
    return
  }

  const exportParams = createExportParams(formattedFileName)

  try {
    const allRowsData = await fetchDataForExport(rowCount)

    handleExportOption(exportParams, allRowsData, formattedFileName)
    toastSuccess(`Exported ${rowCount} rows to ${formattedFileName}.`)
  } catch (err) {
    handleExportError(err)
  } finally {
    isExporting.value = false
  }
}
</script>
